import React, { useState, useEffect } from 'react';
import { X, Save, AlertTriangle, Info, Tag } from 'lucide-react';

// ----------------------------------------------------------------
// ArtSchoolEnrollModal
// Enrolls an art school student in a class.
// Fetches their other active enrollments first to show multi-class
// discount warning and pre-populate discount if applicable.
// ----------------------------------------------------------------
const ArtSchoolEnrollModal = ({ student, family, classes, academicYear, onClose, onSave, apiCall }) => {
  const [form, setForm] = useState({
    classID:       '',
    billingType:   'Monthly',
    discountPct:   '0',
    discountReason: '',
    startDate:     new Date().toISOString().split('T')[0],
    autoRenew:     true,
    notes:         ''
  });

  const [otherEnrollments, setOtherEnrollments] = useState([]);
  const [selectedClass, setSelectedClass]       = useState(null);
  const [saving, setSaving]                     = useState(false);
  const [error, setError]                       = useState('');

  // Derived pricing
  const basePrice = selectedClass
    ? (form.billingType === 'Monthly'
        ? selectedClass.MonthlyPrice
        : selectedClass.Price3Month)
    : null;
  const discountAmt = basePrice
    ? parseFloat((basePrice * (parseFloat(form.discountPct) || 0) / 100).toFixed(2))
    : 0;
  const finalPrice = basePrice ? parseFloat((basePrice - discountAmt).toFixed(2)) : null;

  // ----------------------------------------------------------------
  // Fetch other active enrollments for this student
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!student?.ArtStudentID) return;
    apiCall(`/artschool/students/${student.ArtStudentID}/other-enrollments?academicYear=${academicYear}`)
      .then(data => {
        setOtherEnrollments(data);
        // If student is already in 1+ class, pre-fill 10% multi-class discount
        if (data.length > 0) {
          setForm(f => ({
            ...f,
            discountPct:    '10',
            discountReason: 'Multi-class'
          }));
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student, academicYear]);

  // Update selectedClass when classID changes
  useEffect(() => {
    if (!form.classID) { setSelectedClass(null); return; }
    const cls = classes.find(c => c.ClassID === parseInt(form.classID));
    setSelectedClass(cls || null);
  }, [form.classID, classes]);

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const handleSave = async () => {
    setError('');
    if (!form.classID) { setError('Please select a class.'); return; }
    if (!basePrice)    { setError('No pricing defined for this class type. Check category settings.'); return; }

    setSaving(true);
    try {
      await onSave({
        artStudentID:  student.ArtStudentID,
        familyID:      family.FamilyID,
        classID:       parseInt(form.classID),
        academicYear,
        billingType:   form.billingType,
        basePrice,
        discountPct:   parseFloat(form.discountPct) || 0,
        discountReason: form.discountReason || null,
        startDate:     form.startDate,
        autoRenew:     form.autoRenew,
        notes:         form.notes || null
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to enroll. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Enroll Student</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {student.FirstName} {student.LastName} ·{' '}
              {family.FatherName || family.MotherName || family.FamilyID}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">

          {/* Multi-class discount warning */}
          {otherEnrollments.length > 0 && (
            <div className="flex gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle size={18} className="text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  This student is already enrolled in {otherEnrollments.length} other class{otherEnrollments.length > 1 ? 'es' : ''}
                </p>
                <ul className="text-xs text-amber-700 mt-1 space-y-0.5">
                  {otherEnrollments.map(e => (
                    <li key={e.EnrollmentID}>• {e.ClassName} ({e.CategoryName}) — ${e.FinalPrice}/{e.BillingType}</li>
                  ))}
                </ul>
                <p className="text-xs text-amber-600 mt-1">
                  10% multi-class discount pre-applied. Adjust below if needed.
                </p>
              </div>
            </div>
          )}

          {/* Class Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Class *</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
              value={form.classID}
              onChange={e => set('classID', e.target.value)}
            >
              <option value="">Select a class...</option>
              {classes.map(c => (
                <option key={c.ClassID} value={c.ClassID}>
                  {c.ClassName} — {c.CategoryName} ({c.ClassType})
                  {c.Schedule ? ` · ${c.Schedule}` : ''}
                  {c.TeacherName ? ` · ${c.TeacherName}` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Class details preview */}
          {selectedClass && (
            <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100 text-sm">
              <div className="grid grid-cols-2 gap-2 text-indigo-800">
                <div><span className="text-indigo-500">Type:</span> {selectedClass.ClassType}</div>
                {selectedClass.Schedule && <div><span className="text-indigo-500">Schedule:</span> {selectedClass.Schedule}</div>}
                {selectedClass.TeacherName && <div><span className="text-indigo-500">Teacher:</span> {selectedClass.TeacherName}</div>}
                <div><span className="text-indigo-500">Enrolled:</span> {selectedClass.EnrolledCount || 0}{selectedClass.MaxStudents ? `/${selectedClass.MaxStudents}` : ''}</div>
              </div>
            </div>
          )}

          {/* Billing Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Billing Type *</label>
            <div className="grid grid-cols-2 gap-2">
              {['Monthly', '3Month'].map(type => {
                const price = selectedClass
                  ? (type === 'Monthly' ? selectedClass.MonthlyPrice : selectedClass.Price3Month)
                  : null;
                return (
                  <button
                    key={type}
                    onClick={() => set('billingType', type)}
                    className={`py-3 px-4 rounded-lg border text-sm font-medium transition-colors
                      ${form.billingType === type
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'}`}
                  >
                    <div>{type === 'Monthly' ? 'Monthly' : '3-Month Block'}</div>
                    {price && <div className="text-xs mt-0.5 opacity-80">${price}</div>}
                    {selectedClass && !price && <div className="text-xs mt-0.5 opacity-60">Not available</div>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Discount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
              <Tag size={14} /> Discount
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    className="flex-1 px-3 py-2 outline-none"
                    value={form.discountPct}
                    onChange={e => set('discountPct', e.target.value)}
                  />
                  <span className="px-3 py-2 bg-gray-50 border-l text-gray-500 text-sm">%</span>
                </div>
                {/* Quick presets */}
                <div className="flex gap-1 mt-1">
                  {[0, 10, 15, 20].map(v => (
                    <button
                      key={v}
                      onClick={() => set('discountPct', String(v))}
                      className={`text-xs px-2 py-0.5 rounded border transition-colors
                        ${parseFloat(form.discountPct) === v
                          ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                          : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                    >
                      {v}%
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 text-sm"
                  value={form.discountReason}
                  onChange={e => set('discountReason', e.target.value)}
                >
                  <option value="">No reason</option>
                  <option value="Multi-class">Multi-class</option>
                  <option value="Sibling">Sibling</option>
                  <option value="Manual">Manual</option>
                </select>
              </div>
            </div>
          </div>

          {/* Price Summary */}
          {basePrice && (
            <div className="bg-gray-50 rounded-lg p-4 border">
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Base Price</span>
                  <span>${basePrice.toFixed(2)}</span>
                </div>
                {discountAmt > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount ({form.discountPct}%)</span>
                    <span>-${discountAmt.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-gray-800 pt-1 border-t">
                  <span>Final Price ({form.billingType === 'Monthly' ? '/month' : '/3 months'})</span>
                  <span className="text-indigo-700">${finalPrice?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {selectedClass && !basePrice && (
            <div className="flex gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <Info size={16} className="shrink-0 mt-0.5" />
              No pricing defined for {form.billingType} billing in this category. 
              Update the category pricing first.
            </div>
          )}

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
            <input
              type="date"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
              value={form.startDate}
              onChange={e => set('startDate', e.target.value)}
            />
          </div>

          {/* Auto Renew */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="autoRenew"
              checked={form.autoRenew}
              onChange={e => set('autoRenew', e.target.checked)}
              className="w-4 h-4 rounded text-indigo-600"
            />
            <label htmlFor="autoRenew" className="text-sm text-gray-700">
              Auto-renew monthly
            </label>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
              rows={2}
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              placeholder="Any notes..."
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertTriangle size={16} />
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-5 border-t bg-gray-50 rounded-b-xl sticky bottom-0">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !form.classID || !basePrice}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            <Save size={16} />
            {saving ? 'Enrolling...' : 'Enroll Student'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArtSchoolEnrollModal;
